import {PublishedWorkTerms} from './PublishedWorkTerms';
import { ContractorCalendar } from './ContractorCalendar';
import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { jobRequestUrl } from '@/lib/jobRequests';
import { toast } from 'sonner';

const PreferredContext = createContext(null);
export const usePreferred = () => useContext(PreferredContext);
export const isPreferred = (items, target) => items.some(p => p.discoverable && p.owner_kind === target.owner_kind &&
  (target.user_uuid ? p.user_uuid === target.user_uuid : p.owner_id === target.owner_id));
export const preferredFirst = (items, results, targetOf) => [...results].sort((a, b) =>
  Number(isPreferred(items, targetOf(b))) - Number(isPreferred(items, targetOf(a))));

export function PreferredProvider({ children, projectOrgUuid }) {
  const { user } = useAuth();
  const enabled = user?.roles?.some(r => ['client', 'super_admin'].includes(r));
  const [orgs, setOrgs] = useState([]);
  const [org, setOrg] = useState('');
  const [saved, setSaved] = useState({ org: '', items: [] });
  const [loading, setLoading] = useState(!!enabled);
  const [error, setError] = useState('');
  const [pending, setPending] = useState(false);
  const activeOrg = useRef(org); activeOrg.current = org;
  const items = saved.org === org ? saved.items : [];
  useEffect(() => {
    let active = true;
    if (!enabled) return;
    api('/me/orgs').then(r => {
      if (!active) return;
      const clients = (r.data || []).filter(o => o.kind === 'client');
      setOrgs(clients); setOrg(clients[0]?.uuid || '');
      if (!clients.length) setLoading(false);
    }).catch(e => { if (active) { setError(e.message); setLoading(false); } });
    return () => { active = false; };
  }, [enabled, user?.uuid]);
  useEffect(() => {
    if (projectOrgUuid && orgs.some(o => o.uuid === projectOrgUuid)) setOrg(projectOrgUuid);
  }, [projectOrgUuid, orgs]);
  const refresh = async (scope) => {
    const r = await api(`/orgs/${scope}/preferred-contractors`);
    if (activeOrg.current === scope) setSaved({ org: scope, items: r.items || [] });
  };
  useEffect(() => {
    if (!org) return;
    let active = true;
    setLoading(true); setError('');
    refresh(org).catch(e => { if (active) setError(e.message); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [org]);
  const change = async (target, removeId) => {
    if (!org || pending || loading || error) return;
    const scope = org; setPending(true);
    try {
      const found = items.find(p => isPreferred([p], target || {}));
      const id = removeId || found?.id;
      await api(`/orgs/${scope}/preferred-contractors${id ? `/${id}` : ''}`, {
        method: id ? 'DELETE' : 'PUT', ...(id ? {} : { body: target }),
      });
      await refresh(scope);
      toast.success(id ? 'Removed from preferred contractors' : 'Added to preferred contractors');
    } catch (e) { toast.error(e.message); }
    finally { setPending(false); }
  };
  return <PreferredContext.Provider value={{ enabled, orgs, org, setOrg, items, loading, error, pending, change }}>
    {children}
  </PreferredContext.Provider>;
}

export function PreferredButton({ target }) {
  const ctx = usePreferred();
  if (!ctx?.enabled) return null;
  const selected = isPreferred(ctx.items, target);
  return <button type="button" className={`avcp-btn ${selected ? 'primary' : 'ghost'} text-sm`}
    aria-pressed={selected} disabled={!ctx.org || ctx.loading || ctx.pending || !!ctx.error}
    title={selected ? 'Remove from your organization’s preferred contractors' : 'Save for your client organization'}
    onClick={() => ctx.change(target)}>{selected ? '★ Preferred' : '☆ Add to preferred'}</button>;
}

export function PreferredPanel({ jobId, planning, matches=[] }) {
  const ctx = usePreferred();
  if (!ctx?.enabled) return null;
  return <section className="avcp-card p-5 mb-6" aria-labelledby="preferred-heading">
    <div className="flex flex-wrap items-center justify-between gap-3">
      <h2 id="preferred-heading" className="text-xl font-semibold">Your preferred contractors</h2>
      {ctx.orgs.length > 1 ? <label className="text-sm">Client organization
        <select className="avcp-input" value={ctx.org} disabled={ctx.pending} onChange={e => ctx.setOrg(e.target.value)}>
          {ctx.orgs.map(o => <option key={o.uuid} value={o.uuid}>{o.name}</option>)}
        </select></label> : <span className="text-sm text-[var(--avcp-ink-soft)]">{ctx.orgs[0]?.name}</span>}
    </div>
    <p className="text-sm text-[var(--avcp-ink-soft)] mt-2 mb-4">Start with the people you know. Other contractors are still shown below. Select a project and send a job request to the contractor you want.</p>
    {ctx.error ? <p role="alert">Could not load preferred contractors: {ctx.error} <button className="underline" onClick={() => window.location.reload()}>Try again</button></p>
      : ctx.loading ? <p role="status">Loading preferred contractors…</p>
      : !ctx.org ? <p>Create a client organization on <Link className="underline" to="/app">Home</Link> to save your preferred contractors.</p>
      : ctx.items.length === 0 ? <p className="text-sm">Choose <strong>☆ Add to preferred</strong> on a company or lead below. Your organization’s saved list will appear here.</p>
      : <div className="grid sm:grid-cols-2 gap-3">{ctx.items.map(p => <article key={p.id} className="border border-[var(--avcp-line)] rounded-xl p-4">
        <h3 className="font-semibold">{p.display_name}</h3>
        <p className="text-sm mt-1">{!p.discoverable ? 'Their profile is no longer visible. Choose another contractor below.' : p.operational_status === 'vacation' ? 'On vacation — check other contractors below.' : 'Availability needs confirmation for your project dates.'}</p>
        {matches.filter(m=>m.owner_kind===p.owner_kind&&m.owner_id===p.owner_id).map(m=><p key={`${m.owner_kind}-${m.owner_id}`} className="text-sm mt-2 font-medium">Matches {m.matched_count} of {m.total_requirements} requested roles{m.fully_covers?' · Full crew match':''}</p>)}
        {p.discoverable&&<PublishedWorkTerms userUuid={p.user_uuid} companyId={p.owner_kind==='org'?p.owner_id:undefined}/>}
        {p.discoverable&&<ContractorCalendar target={{owner_kind:p.owner_kind,owner_id:p.owner_id,...(p.user_uuid?{user_uuid:p.user_uuid}:{})}} name={p.display_name}/>}
        <div className="flex flex-wrap gap-2 mt-3">
          {p.discoverable && <Link className="avcp-btn primary" to={jobRequestUrl(p, jobId)} state={{planning}}>Send job request</Link>}
          <button className="avcp-btn ghost" disabled={ctx.pending || ctx.loading} onClick={() => ctx.change(null, p.id)} aria-label={`Remove ${p.display_name} from preferred`}>★ Preferred · Remove</button>
        </div>
      </article>)}</div>}
    <p className="text-xs text-[var(--avcp-ink-soft)] mt-4">Shared with your client organization. A preferred label is your choice; it does not verify qualifications or reserve anyone.</p>
  </section>;
}
